import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface FiscalDocumentLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  lineTaxes?: { taxName: string; ratePercent: number; taxAmount: number }[];
}

@Component({
  selector: 'app-fiscal-document-lines',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './fiscal-document-lines.component.html',
  styleUrls: ['./fiscal-document-lines.component.scss'],
  host: {
    '[class.fd-lines--view]': 'isViewMode'
  }
})
export class FiscalDocumentLinesComponent {
  @Input() items: FiscalDocumentLineItem[] = [];
  @Input() isViewMode = false;
  @Input() itemsCount = 0; // Para controlar el botón eliminar (no eliminar si es el único)

  @Output() addItem = new EventEmitter<void>();
  @Output() editItem = new EventEmitter<number>();
  @Output() removeItem = new EventEmitter<number>();

  get columns(): string[] {
    const base = ['description', 'quantity', 'unitPrice', 'subtotal', 'taxRate', 'taxAmount', 'total'];
    return this.isViewMode ? base : [...base, 'actions'];
  }

  /** Indica si la descripción debe acortarse en modo vista. */
  shouldShortenDescription(description: string | null | undefined): boolean {
    if (!description) {
      return false;
    }
    const trimmed = description.trim();
    if (trimmed.length <= 48) {
      return false;
    }
    return this.isCalculationDescription(trimmed);
  }

  getShortDescription(description: string | null | undefined): string {
    if (!description) {
      return '—';
    }
    const trimmed = description.trim();
    if (!this.shouldShortenDescription(trimmed)) {
      return trimmed;
    }

    const parsedStay = this.tryParseStayDescription(trimmed);
    if (parsedStay) {
      return parsedStay;
    }

    const emDashIdx = trimmed.indexOf('—');
    if (emDashIdx > 0) {
      return trimmed.slice(0, emDashIdx).trim();
    }

    const hyphenIdx = trimmed.indexOf(' - ');
    if (hyphenIdx > 0) {
      return trimmed.slice(0, hyphenIdx).trim();
    }

    const calcIdx = trimmed.search(/\bCalculado\b/i);
    if (calcIdx > 0) {
      return trimmed.slice(0, calcIdx).trim().replace(/[—\-:,]+$/, '').trim();
    }

    return trimmed.length > 48 ? `${trimmed.slice(0, 45).trim()}…` : trimmed;
  }

  private isCalculationDescription(description: string): boolean {
    if (description.length > 48) {
      return true;
    }
    return /\b(calculado|horaria|nocturna|diaria|estad[ií]a|tarifa|d[ií]as?|horas?)\b/i.test(description);
  }

  private tryParseStayDescription(description: string): string | null {
    const stayMatch = description.match(/estad[ií]a\s+([^\s—\-]+)/i);
    if (!stayMatch?.[1]) {
      return null;
    }
    const plate = stayMatch[1].trim();
    const daysMatch = description.match(/(\d+)\s*d[ií]a\(s\)|(\d+)\s*d[ií]as?\b/i);
    const days = daysMatch?.[1] ?? daysMatch?.[2];
    return days ? `Estadía ${plate} · ${days} días` : `Estadía ${plate}`;
  }
}
